import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getOrganizationDashboardDefaultRange } from '@kirin/api-client/api/organizations';
import type { organizationDashboard } from '@kirin/api-client/contracts';

import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';

type DashboardDefaultRangeResponse =
  organizationDashboard.DashboardDefaultRangeResponse;

/** How long the opening range is reused (the server caches it 10 minutes). */
const DASHBOARD_DEFAULT_RANGE_STALE_MS = 10 * 60_000;

/**
 * KIR-5693: the range the dashboard's time-ranged widgets open on (`1d` for a
 * very busy organization, else `7d`). Not retried: on an error the dashboard
 * opens on its usual 7 days rather than wait.
 */
export function useDashboardDefaultRange(
  organizationId: string | undefined
): UseQueryResult<DashboardDefaultRangeResponse, Error> {
  return useQuery<DashboardDefaultRangeResponse, Error>({
    queryKey: queryKeys.organizations.dashboardDefaultRange(
      organizationId ?? ''
    ),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return getOrganizationDashboardDefaultRange(apiClient, organizationId);
    },
    enabled: !!organizationId,
    staleTime: DASHBOARD_DEFAULT_RANGE_STALE_MS,
    retry: false,
  });
}
